import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getWcPool,
  getWcLeaderboard,
  getWcSwapEligible,
  applyWcSwap,
} from "../api/worldCup";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";
const WC_SEASON_ID = 2;

function FlagImg({ code, size = 24 }) {
  if (!code) return null;
  return (
    <img
      src={`https://flagcdn.com/${Math.round(size * 1.33)}x${size}/${code.toLowerCase()}.png`}
      alt={code}
      style={{ width: size * 1.33, height: size, objectFit: "cover", borderRadius: 2, display: "inline-block" }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

export default function WorldCupSwapPage() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);


  const [myTeams,        setMyTeams]        = useState([]);
  const [swapUsed,       setSwapUsed]       = useState(false);
  const [eligibleTeams,  setEligibleTeams]  = useState([]);
  const [swapWindowOpen, setSwapWindowOpen] = useState(null); // null = loading
  const [loadingPage,    setLoadingPage]    = useState(true);
  const [pageError,      setPageError]      = useState("");

  const [selectedRemoveId, setSelectedRemoveId] = useState(null);
  const [addCandidate,     setAddCandidate]     = useState(null);
  const [applying,         setApplying]         = useState(false);
  const [applyError,       setApplyError]       = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey,     setSortKey]     = useState("group_stage_points");
  const [sortDir,     setSortDir]     = useState("desc");

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function sortIndicator(key) {
    if (sortKey !== key) return " ↕";
    return sortDir === "desc" ? " ↓" : " ↑";
  }

  useEffect(() => {
    async function load() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        // The pool fetch's result is unused, but the call is kept: it rejects
        // for non-members, so it doubles as the page's access check.
        const [, lb] = await Promise.all([
          getWcPool(WC_SEASON_ID, numericPoolId),
          getWcLeaderboard(WC_SEASON_ID, numericPoolId),
        ]);

        const myEntry = lb.find((e) => e.user_id === user?.id);
        if (myEntry) {
          setMyTeams(myEntry.teams || []);
          setSwapUsed(!!myEntry.swap_used);
        }

        try {
          const eligible = await getWcSwapEligible(WC_SEASON_ID, numericPoolId);
          setEligibleTeams(eligible);
          setSwapWindowOpen(true);
        } catch {
          setSwapWindowOpen(false);
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    load();
  }, [numericPoolId, user?.id]);

  const selectedRemoveTeam = useMemo(
    () => myTeams.find((t) => t.team_id === selectedRemoveId) || null,
    [myTeams, selectedRemoveId]
  );

  const displayedEligible = useMemo(() => {
    let list = [...eligibleTeams];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.group || "").toLowerCase().includes(q) ||
          (t.abbreviation || "").toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      const cmp = typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [eligibleTeams, searchQuery, sortKey, sortDir]);

  function handleSelectRemove(teamId) {
    setSelectedRemoveId((prev) => (prev === teamId ? null : teamId));
    setAddCandidate(null);
    setApplyError("");
  }

  function handleSelectAdd(team) {
    setAddCandidate(team);
    setApplyError("");
    onOpen();
  }

  async function handleConfirmSwap() {
    setApplying(true);
    setApplyError("");
    try {
      await applyWcSwap(WC_SEASON_ID, numericPoolId, selectedRemoveId, addCandidate.team_id);
      onClose();
      navigate(`/world-cup/current/${numericPoolId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Swap failed.";
      setApplyError(typeof msg === "string" ? msg : "Swap failed.");
    } finally {
      setApplying(false);
    }
  }

  function handleModalClose() {
    if (applying) return;
    onClose();
    setAddCandidate(null);
    setApplyError("");
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading swap page...</Text>
        </Flex>
      </Box>
    );
  }

  const swapLocked = swapWindowOpen === false;
  const contentOpacity       = swapLocked || swapUsed ? 0.45 : 1;
  const contentPointerEvents = swapLocked || swapUsed ? "none" : "auto";

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="WORLD CUP"
        onBack={() => navigate("/world-cup")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Page Body ── */}
      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        <HStack mb={6} spacing={3}>
          <Button
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/world-cup/current/${numericPoolId}`)}
            _hover={{ bg: "gray.100" }}
          >
            Leaderboard View
          </Button>
        </HStack>

        {/* Status banners */}
        {swapLocked && (
          <Alert status="info" borderRadius="md" mb={6}>
            <AlertIcon />
            Swap is not available until after the group stage is complete and the swap window opens.
          </Alert>
        )}
        {swapUsed && (
          <Alert status="warning" borderRadius="md" mb={6}>
            <AlertIcon />
            You have already used your one allowed swap for this pool.
          </Alert>
        )}

        {/* ── Main content (grayed out when locked or swap used) ── */}
        <Box opacity={contentOpacity} pointerEvents={contentPointerEvents}>
          <HStack align="start" spacing={6} flexWrap="wrap">

            {/* LEFT — user's countries */}
            <Box
              flex="1"
              minW="280px"
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={6}
            >
              <Heading size="md" color={TOP_BAR_BLUE} mb={1}>Your Countries</Heading>
              <Text fontSize="xs" color="gray.500" mb={4}>
                Click a country to select it for removal and see eligible replacements.
              </Text>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="44px" px={1}></Th>
                    <Th>Country</Th>
                    <Th isNumeric>W-D</Th>
                    <Th isNumeric>Pts</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {myTeams.length === 0 ? (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.400">No teams found.</Text>
                      </Td>
                    </Tr>
                  ) : (
                    myTeams.map((t) => {
                      const isSelected = t.team_id === selectedRemoveId;
                      return (
                        <Tr
                          key={t.team_id}
                          cursor="pointer"
                          bg={isSelected ? "red.50" : "transparent"}
                          _hover={{ bg: isSelected ? "red.100" : "gray.50" }}
                          onClick={() => handleSelectRemove(t.team_id)}
                          opacity={t.is_active ? 1 : 0.45}
                        >
                          <Td w="44px" px={1}>
                            {t.flag_code && <FlagImg code={t.flag_code} size={24} />}
                          </Td>
                          <Td fontWeight={isSelected ? "bold" : "normal"}>
                            {t.team_name}
                            {!t.is_active && (
                              <Badge ml={2} colorScheme="gray" fontSize="2xs">OUT</Badge>
                            )}
                            {t.is_swapped_out && (
                              <Badge ml={2} colorScheme="purple" fontSize="2xs">SWAPPED</Badge>
                            )}
                            {isSelected && (
                              <Badge ml={2} colorScheme="red" fontSize="2xs">REMOVE?</Badge>
                            )}
                          </Td>
                          <Td isNumeric>
                            {t.group_stage?.wins ?? "—"}-{t.group_stage?.draws ?? "—"}
                          </Td>
                          <Td isNumeric fontWeight="bold">{t.points}</Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* RIGHT — eligible replacements */}
            <Box
              flex="3"
              minW="400px"
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={6}
            >
              {!selectedRemoveTeam ? (
                <Flex h="200px" align="center" justify="center">
                  <Text color="gray.400" fontSize="lg">
                    ← Select a country to remove to see eligible replacements
                  </Text>
                </Flex>
              ) : (
                <>
                  <Heading size="md" color={TOP_BAR_BLUE} mb={1}>Available Countries</Heading>
                  <Text fontSize="xs" color="gray.500" mb={3}>
                    Countries that advanced from the group stage and are not drafted in this pool.
                    Click a country to confirm the swap.
                  </Text>
                  <InputGroup mb={3} size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by name or group..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      bg="gray.50"
                      borderColor="gray.200"
                    />
                  </InputGroup>
                  <Box overflowX="auto" overflowY="auto" maxH="520px">
                    <Table size="sm" variant="simple">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th w="44px" px={1}></Th>
                          <Th
                            cursor="pointer"
                            onClick={() => toggleSort("name")}
                            userSelect="none"
                          >
                            Country{sortIndicator("name")}
                          </Th>
                          <Th
                            cursor="pointer"
                            onClick={() => toggleSort("group")}
                            userSelect="none"
                          >
                            Grp{sortIndicator("group")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("fifa_rank")}
                            userSelect="none"
                          >
                            Rank{sortIndicator("fifa_rank")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("wins")}
                            userSelect="none"
                          >
                            W{sortIndicator("wins")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("draws")}
                            userSelect="none"
                          >
                            D{sortIndicator("draws")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("goals_scored")}
                            userSelect="none"
                          >
                            GF{sortIndicator("goals_scored")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("goals_allowed")}
                            userSelect="none"
                          >
                            GA{sortIndicator("goals_allowed")}
                          </Th>
                          <Th
                            isNumeric
                            cursor="pointer"
                            onClick={() => toggleSort("group_stage_points")}
                            userSelect="none"
                          >
                            GS Pts{sortIndicator("group_stage_points")}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {displayedEligible.length > 0 ? (
                          displayedEligible.map((t) => (
                            <Tr
                              key={t.team_id}
                              cursor="pointer"
                              _hover={{ bg: "blue.50" }}
                              onClick={() => handleSelectAdd(t)}
                            >
                              <Td w="44px" px={1}>
                                {t.flag_code && <FlagImg code={t.flag_code} size={24} />}
                              </Td>
                              <Td fontWeight="medium">{t.name}</Td>
                              <Td fontWeight="semibold">{t.group || "—"}</Td>
                              <Td isNumeric>{t.fifa_rank ?? "—"}</Td>
                              <Td isNumeric>{t.wins ?? 0}</Td>
                              <Td isNumeric>{t.draws ?? 0}</Td>
                              <Td isNumeric>{t.goals_scored ?? 0}</Td>
                              <Td isNumeric>{t.goals_allowed ?? 0}</Td>
                              <Td isNumeric fontWeight="bold">{t.group_stage_points ?? 0}</Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={9}>
                              <Text color="gray.500">
                                {searchQuery
                                  ? "No countries match your search."
                                  : "No eligible countries found."}
                              </Text>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>

          </HStack>
        </Box>
      </Container>

      {/* ── Confirm Swap Modal ── */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent
          bg="white"
          color="black"
          border={`3px solid ${TOP_BAR_BLUE}`}
          borderRadius="lg"
        >
          <ModalHeader color={TOP_BAR_BLUE}>Confirm Swap</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} isDisabled={applying} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Table size="sm" variant="simple">
                <Tbody>
                  <Tr>
                    <Td color="red.600" fontWeight="bold">Remove</Td>
                    <Td>
                      <HStack spacing={2}>
                        {selectedRemoveTeam?.flag_code && (
                          <FlagImg code={selectedRemoveTeam.flag_code} size={24} />
                        )}
                        <Text>{selectedRemoveTeam?.team_name}</Text>
                      </HStack>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="green.600" fontWeight="bold">Add</Td>
                    <Td>
                      <HStack spacing={2}>
                        {addCandidate?.flag_code && (
                          <FlagImg code={addCandidate.flag_code} size={24} />
                        )}
                        <Text>{addCandidate?.name}</Text>
                      </HStack>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
              <Text fontSize="xs" color="gray.500">
                This is your one allowed swap for this pool. Points accumulated by the removed
                country are forfeited. This action cannot be undone.
              </Text>
              {applyError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />{applyError}
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleModalClose} isDisabled={applying}>
                Cancel
              </Button>
              <Button
                bg="red.600"
                color="white"
                _hover={{ bg: "red.700" }}
                onClick={handleConfirmSwap}
                isLoading={applying}
                loadingText="Applying..."
              >
                Confirm Swap
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
}
